import { Document, Page, Text, View } from '@react-pdf/renderer';
import { useMemo } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { UseGetUserInfo } from 'src/hooks/userGetUserInfo';
import { SaleInvoiceFormData } from 'src/modals/SaleDetails/actions/SaleInvoice/SaleInvoiceOptionsPopover';
import { ContentId } from 'src/utils/constants/contentId';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import {
  Event,
  ListingDetails,
  Performer,
  SaleDetails,
  Venue,
} from 'src/WebApiController';

import {
  CostsAndCredits,
  InvoiceAddress,
  InvoiceLabeledField,
  InvoiceLabeledSection,
  InvoiceLineTable,
  InvoicePayment,
} from './SaleInvoicePDF.components';
import { styles } from './SaleInvoicePDF.css';
import { SaleInvoicePayments } from './SaleInvoicePDF.types';
import { formatDate } from './SaleInvoicePDF.utils';

interface SaleInvoicePDFProps {
  event: Event;
  performer?: Performer;
  venue?: Venue;
  sale: SaleDetails;
  invoiceFormData: SaleInvoiceFormData;
  buyer?: UseGetUserInfo;
  saleListing?: ListingDetails | null;
}

export const SaleInvoicePDF = ({
  sale,
  event,
  performer,
  venue,
  buyer,
  invoiceFormData,
  saleListing,
}: SaleInvoicePDFProps) => {
  const payments = useMemo((): SaleInvoicePayments => {
    const currency = sale.currency;
    const chargesAndCredits =
      -(sale.charges?.amt ?? 0) + (sale.credits?.amt ?? 0);
    return {
      costAndCredit: applyNumberFormatting({
        inputNumber: chargesAndCredits,
        currencyCode: currency,
      }),
      invoiceTotal: applyNumberFormatting({
        inputNumber: chargesAndCredits + (sale.soldTktCost?.amt ?? 0),
        currencyCode: currency,
      }),
      outstandingBalance: applyNumberFormatting({
        inputNumber: sale.ttlNetProcs?.amt ?? 0,
        currencyCode: currency,
      }),
    };
  }, [
    sale.charges?.amt,
    sale.credits?.amt,
    sale.currency,
    sale.soldTktCost?.amt,
    sale.ttlNetProcs?.amt,
  ]);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>
            <Content id={ContentId.InvoiceDetails} />
          </Text>
          <View style={[styles.detailsGrid, { marginBottom: 20 }]}>
            <InvoiceLabeledField
              labelContentId={ContentId.InvoiceNumber}
              value={sale.idOnMkp}
            />
            <InvoiceLabeledField
              labelContentId={ContentId.InternalId}
              value={sale.id}
            />
            <InvoiceLabeledField
              labelContentId={ContentId.DateCreated}
              value={formatDate(sale.saleDate)}
            />
            <InvoiceLabeledField
              labelContentId={ContentId.DueDate}
              value={formatDate(invoiceFormData.dueDate, false)}
            />
          </View>
          <View style={styles.detailsGrid}>
            <InvoiceLabeledField
              labelContentId={ContentId.FulfillmentState}
              value={sale.fulfillState}
            />
            <InvoiceLabeledField
              labelContentId={ContentId.ExternalReference}
              value={sale.idOnMkp}
            />
            <InvoiceLabeledField
              labelContentId={ContentId.InHandDate}
              value={formatDate(sale.inHandAt)}
            />
            <InvoiceLabeledField labelContentId={undefined} value={undefined} />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Customer: {buyer?.name}</Text>
          <View style={styles.detailsGrid}>
            <InvoicePayment
              paymentTerms={invoiceFormData.paymentTerms}
              paymentType={invoiceFormData.paymentType}
              deliveryMethod={
                sale.ticketType ? sale.ticketType.toString() : undefined
              }
              email={buyer?.email}
            />
            <InvoiceLabeledSection labelContentId={ContentId.BillingAddress}>
              <InvoiceAddress
                invoiceAddress={
                  invoiceFormData.sameAddress
                    ? invoiceFormData.shipping
                    : invoiceFormData.billing
                }
              />
            </InvoiceLabeledSection>
            <InvoiceLabeledSection labelContentId={ContentId.ShippingAddress}>
              <InvoiceAddress invoiceAddress={invoiceFormData.shipping} />
            </InvoiceLabeledSection>
          </View>
        </View>

        <InvoiceLineTable
          sale={sale}
          event={event}
          performer={performer}
          venue={venue}
        />

        <CostsAndCredits
          sale={sale}
          payments={payments}
          saleListing={saleListing}
        />

        {/* Financial Summary */}
        <View style={styles.summary}>
          <View style={{ alignItems: 'flex-end' }}>
            <Text
              style={[styles.textBold, styles.textRightAlign, { fontSize: 16 }]}
            >
              <Content id={ContentId.InvoiceTotal} />: {payments.invoiceTotal}
            </Text>
            <Text
              style={[styles.textBold, styles.textRightAlign, { fontSize: 16 }]}
            >
              <Content id={ContentId.OutstandingBalance} />:{' '}
              {payments.outstandingBalance}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};
